import * as i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { merge } from 'lodash';
import type { Language } from 'korint-sdk/react';
import { sendEvent } from '@dash0/sdk-web';
import frTranslations from './locales/fr/translations.json';
import enTranslations from './locales/en/translations.json';

export const defaultNS = 'translation';

export function getStoredLanguage(): Language {
  try {
    const stored = localStorage.getItem('korint.language') as Language | null;
    return stored ?? 'fr';
  } catch {
    return 'fr';
  }
}

export function persistLanguage(language: Language): void {
  try {
    localStorage.setItem('korint.language', language);
  } catch {
    // localStorage is unavailable (private mode, quota exceeded, disabled).
    // The language change still applies to the current session.
  }
}

const baseResources = {
  common: {
    [defaultNS]: {
      empty: '',
    },
  },
  fr: {
    [defaultNS]: frTranslations,
  },
  en: {
    [defaultNS]: enTranslations,
  },
};

const reportedMissingKeys = new Set<string>();

// A defaultValue means the call site already renders a fallback; a numeric or
// whitespace-bearing key is the raw last-resort value of `t([key, rawValue])`.
export const isReportableMissingKey = (key: string, defaultValue?: unknown) =>
  defaultValue === undefined && !/^\d+$/.test(key) && !/\s/.test(key);

export function initI18n(tenantResourcesOverride?: i18n.Resource) {
  i18n.use(initReactI18next).init({
    defaultNS,
    lng: getStoredLanguage(),
    fallbackLng: ['fr', 'common'],
    resources: merge({}, baseResources, tenantResourcesOverride) as i18n.Resource,
    interpolation: {
      escapeValue: false,
    },
    // Missing keys otherwise fall back silently to the raw key (how ENG-3104/3904 shipped).
    // sendEvent no-ops where Dash0 isn't initialized (e.g. local dev).
    saveMissing: true,
    missingKeyHandler: (_lngs, ns, key, _fallbackValue, _updateMissing, options) => {
      if (!isReportableMissingKey(key, options?.defaultValue)) return;
      const id = `${ns}:${key}`;
      if (reportedMissingKeys.has(id)) return;
      reportedMissingKeys.add(id);
      sendEvent('i18n.missing_translation', {
        severity: 'WARN',
        title: 'Missing i18n translation',
        attributes: { 'i18n.key': key, 'i18n.namespace': ns },
      });
    },
  });

  return i18n;
}
